import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Plus, TriangleAlert } from "lucide-react";
import { FadeIn, MaskText, SectionTag } from "@/components/Reveal";
import { scrollToSection } from "@/App";

const PPS_IMG =
  "https://images.unsplash.com/photo-1770048427702-6b42cdf78b22?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMG1ldGFsJTIwbWFjaGluZSUyMHBhcnRzfGVufDB8fHx8MTc4ODY0MTUyNHww&ixlib=rb-4.1.0&q=85";

const ToolsSection = () => {
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section id="tools" data-testid="tools-section" className="relative py-28 md:py-40">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <SectionTag index="01" label="Our Tools" />

        <h2 data-testid="tools-heading" className="mt-8 font-display font-semibold uppercase leading-[0.9] tracking-tight">
          <MaskText
            lines={[
              <span key="1" className="text-[clamp(3rem,7vw,7rem)] text-white">The Lineup</span>,
              <span key="2" className="text-[clamp(3rem,7vw,7rem)] text-stroke-silver">Starts Here.</span>,
            ]}
          />
        </h2>

        <div className="mt-20 grid grid-cols-1 gap-6 lg:grid-cols-12">
          <FadeIn className="lg:col-span-8" delay={0.1}>
            <article
              data-testid="pps-card"
              className="group relative flex h-full min-h-[560px] flex-col justify-end overflow-hidden border border-white/10 bg-carbon transition-[border-color] duration-500 hover:border-ember/60"
            >
              <div ref={imgRef} className="absolute inset-0 overflow-hidden">
                <motion.img
                  src={PPS_IMG}
                  alt="Machined metal — a first look at the world PrePipe Station is built for"
                  style={{ y: imgY }}
                  className="h-[116%] w-full scale-105 object-cover opacity-45 transition-[opacity,transform] duration-700 group-hover:scale-110 group-hover:opacity-60"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-coal via-coal/55 to-transparent" />
              </div>

              <span className="absolute left-0 top-0 h-8 w-8 border-l-2 border-t-2 border-ember" aria-hidden="true" />
              <span className="absolute right-0 top-0 h-8 w-8 border-r-2 border-t-2 border-ember/50" aria-hidden="true" />

              <div className="relative z-10 p-8 md:p-14">
                <div className="mb-6 flex items-center gap-3">
                  <span className="inline-block h-2 w-2 animate-pulse bg-ember red-glow-box" />
                  <span className="font-body text-[11px] font-medium uppercase tracking-[0.35em] text-ember">
                    Flagship — In Development
                  </span>
                </div>
                <h3 className="font-display text-[clamp(3rem,6vw,6.5rem)] font-semibold uppercase leading-[0.9] text-white">
                  PrePipe <span className="metal-sheen">Station</span>
                </h3>
                <p className="mt-2 font-display text-2xl uppercase tracking-[0.4em] text-ember red-glow-text">PPS</p>
                <p className="mt-6 max-w-lg font-body text-base font-light leading-relaxed text-ash">
                  Our first flagship tool, born from a real problem found on the jobsite.
                  Full reveal coming soon — this is only the beginning of the Proxima lineup.
                </p>
                <div className="mt-9 flex flex-wrap items-center gap-4">
                  <Link
                    data-testid="pps-explore-link"
                    to="/pps"
                    className="group/link inline-flex items-center gap-2 bg-ember px-7 py-3 font-display text-lg font-semibold uppercase tracking-[0.15em] text-white transition-[box-shadow,transform] duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_34px_-6px_rgba(230,0,0,0.65)]"
                  >
                    Explore PPS <ArrowUpRight size={18} className="transition-transform duration-300 group-hover/link:translate-x-1 group-hover/link:-translate-y-1" />
                  </Link>
                  <button
                    data-testid="pps-notify-button"
                    onClick={() => scrollToSection("contact")}
                    className="inline-flex items-center gap-2 border border-steel/30 px-7 py-3 font-display text-lg font-semibold uppercase tracking-[0.15em] text-steel transition-[border-color,color,background-color] duration-300 hover:border-ember hover:bg-ember/10 hover:text-white"
                  >
                    Get Notified
                  </button>
                </div>
              </div>
              <div className="relative z-10 border-t border-white/10">
                <img
                  data-testid="pps-station-image"
                  src="/pps-station.jpg"
                  alt="PrePipe Station (PPS) on a jobsite workbench surrounded by copper fittings and drills"
                  className="mx-auto block h-auto w-full"
                />
                <div className="px-8 pb-10 pt-10 md:px-14">
                  <p className="font-display text-3xl font-semibold uppercase leading-tight text-white md:text-4xl">
                    It isn't another tool. <span className="text-ember red-glow-text">It's a better way to work.</span>
                  </p>
                  <p className="mt-4 font-body text-xs font-medium uppercase tracking-[0.4em] text-steel">
                    Push in. Prep. Pull out.
                  </p>
                </div>
              </div>
            </article>
          </FadeIn>

          <FadeIn className="lg:col-span-4" delay={0.2}>
            <article
              data-testid="next-tools-card"
              className="flex h-full min-h-[272px] flex-col justify-between border border-dashed border-white/15 bg-transparent p-8 transition-[border-color] duration-500 hover:border-ember/50 md:p-10"
            >
              <Plus size={30} className="text-ember" strokeWidth={1.5} />
              <div>
                <h3 className="font-display text-4xl font-semibold uppercase leading-none text-white">What's Next</h3>
                <p className="mt-4 font-body text-sm font-light leading-relaxed text-ash">
                  More tools are already on the drawing board. Every one of them starts the
                  same way — with a real problem, on a real jobsite.
                </p>
              </div>
            </article>
          </FadeIn>
        </div>

        <FadeIn className="mt-6" delay={0.1}>
          <article
            data-testid="mud-sink-card"
            className="group relative grid grid-cols-1 overflow-hidden border border-white/10 bg-carbon transition-[border-color] duration-500 hover:border-ember/60 lg:grid-cols-12"
          >
            <div className="relative overflow-hidden lg:col-span-7">
              <img
                data-testid="mud-sink-image"
                src="/mud-sink.jpg"
                alt="Mud Sink by Proxima Centauri Tools — patent pending, coming soon"
                className="h-auto w-full object-cover transition-transform duration-700 group-hover:scale-[1.03] lg:h-full"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-coal/60 via-transparent to-transparent lg:bg-gradient-to-r lg:from-transparent lg:to-carbon/70" />
            </div>
            <div className="relative flex flex-col justify-center p-8 md:p-14 lg:col-span-5">
              <span className="absolute right-0 top-0 h-8 w-8 border-r-2 border-t-2 border-ember/50" aria-hidden="true" />
              <div className="mb-6 flex items-center gap-3">
                <span className="inline-block h-2 w-2 animate-pulse bg-ember red-glow-box" />
                <span className="font-body text-[11px] font-medium uppercase tracking-[0.35em] text-ember">
                  02 — Next In Line
                </span>
              </div>
              <h3 data-testid="mud-sink-heading" className="font-display text-[clamp(3rem,6vw,6rem)] font-semibold uppercase leading-[0.9] text-white">
                Mud <span className="metal-sheen">Sink</span>
              </h3>
              <p className="mt-3 font-body text-xs font-medium uppercase tracking-[0.4em] text-steel">
                Keep The Jobsite Clean
              </p>
              <p className="mt-6 max-w-md font-body text-base font-light leading-relaxed text-ash">
                The next product from Proxima Centauri Tools. More innovation born on the
                jobsite — full reveal coming.
              </p>
              <div className="mt-9 flex flex-wrap items-center gap-4">
                <span
                  data-testid="mud-sink-patent-badge"
                  className="border border-ember/60 px-5 py-2.5 font-display text-base font-semibold uppercase tracking-[0.2em] text-ember"
                >
                  Patent Pending
                </span>
                <span
                  data-testid="mud-sink-coming-badge"
                  className="bg-ember px-5 py-2.5 font-display text-base font-semibold uppercase tracking-[0.2em] text-white red-glow-box"
                >
                  Coming Soon
                </span>
              </div>
              <Link
                data-testid="mud-sink-explore-link"
                to="/mud-sink"
                className="group/link mt-8 inline-flex w-fit items-center gap-2 border border-steel/30 px-7 py-3 font-display text-lg font-semibold uppercase tracking-[0.15em] text-steel transition-[border-color,color,background-color] duration-300 hover:border-ember hover:bg-ember/10 hover:text-white"
              >
                Explore Mud Sink <ArrowUpRight size={18} className="transition-transform duration-300 group-hover/link:translate-x-1 group-hover/link:-translate-y-1" />
              </Link>
            </div>
          </article>
        </FadeIn>

        <FadeIn className="mt-24 flex items-center gap-6" delay={0.05}>
          <span className="h-px flex-1 bg-gradient-to-r from-transparent via-white/15 to-white/5" aria-hidden="true" />
          <svg width="16" height="14" viewBox="0 0 18 16" className="shrink-0" aria-hidden="true">
            <path d="M9 1 L17 15 H1 Z" fill="none" stroke="#E60000" strokeWidth="1.4" />
          </svg>
          <span className="font-body text-[11px] font-medium uppercase tracking-[0.45em] text-ash">
            Also In Development
          </span>
          <svg width="16" height="14" viewBox="0 0 18 16" className="shrink-0" aria-hidden="true">
            <path d="M9 1 L17 15 H1 Z" fill="none" stroke="#E60000" strokeWidth="1.4" />
          </svg>
          <span className="h-px flex-1 bg-gradient-to-l from-transparent via-white/15 to-white/5" aria-hidden="true" />
        </FadeIn>

        <FadeIn className="mt-16" delay={0.1}>
          <article
            data-testid="sit-buddy-card"
            className="group relative grid grid-cols-1 overflow-hidden border border-white/10 bg-carbon transition-[border-color] duration-500 hover:border-ember/60 lg:grid-cols-12"
          >
            <div className="relative overflow-hidden lg:order-2 lg:col-span-7">
              <img
                data-testid="sit-buddy-image"
                src="/sit-buddy.jpg"
                alt="Sit Buddy by Proxima Centauri Tools — the bathtub workstation, patent pending, coming soon"
                className="h-auto w-full object-cover transition-transform duration-700 group-hover:scale-[1.03] lg:h-full"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-coal/60 via-transparent to-transparent lg:bg-gradient-to-l lg:from-transparent lg:to-carbon/70" />
            </div>
            <div className="relative flex flex-col justify-center p-8 md:p-14 lg:order-1 lg:col-span-5">
              <span className="absolute left-0 top-0 h-8 w-8 border-l-2 border-t-2 border-ember/50" aria-hidden="true" />
              <div className="mb-6 flex items-center gap-3">
                <span className="inline-block h-2 w-2 animate-pulse bg-ember red-glow-box" />
                <span className="font-body text-[11px] font-medium uppercase tracking-[0.35em] text-ember">
                  03 — In The Pipeline
                </span>
              </div>
              <h3 data-testid="sit-buddy-heading" className="font-display text-[clamp(3rem,6vw,6rem)] font-semibold uppercase leading-[0.9] text-white">
                Sit <span className="metal-sheen">Buddy</span>
              </h3>
              <p className="mt-3 font-body text-xs font-medium uppercase tracking-[0.4em] text-steel">
                The Bathtub Workstation
              </p>
              <p className="mt-6 max-w-md font-body text-base font-light leading-relaxed text-ash">
                Another product from Proxima Centauri Tools. Turn any tub into a workstation —
                full reveal coming.
              </p>
              <div className="mt-9 flex flex-wrap items-center gap-4">
                <span
                  data-testid="sit-buddy-patent-badge"
                  className="border border-ember/60 px-5 py-2.5 font-display text-base font-semibold uppercase tracking-[0.2em] text-ember"
                >
                  Patent Pending
                </span>
                <span
                  data-testid="sit-buddy-coming-badge"
                  className="bg-ember px-5 py-2.5 font-display text-base font-semibold uppercase tracking-[0.2em] text-white red-glow-box"
                >
                  Coming Soon
                </span>
              </div>
              <Link
                data-testid="sit-buddy-explore-link"
                to="/sit-buddy"
                className="group/link mt-8 inline-flex w-fit items-center gap-2 border border-steel/30 px-7 py-3 font-display text-lg font-semibold uppercase tracking-[0.15em] text-steel transition-[border-color,color,background-color] duration-300 hover:border-ember hover:bg-ember/10 hover:text-white"
              >
                Explore Sit Buddy <ArrowUpRight size={18} className="transition-transform duration-300 group-hover/link:translate-x-1 group-hover/link:-translate-y-1" />
              </Link>
            </div>
          </article>
        </FadeIn>

        <FadeIn className="mt-24 flex items-center gap-6" delay={0.05}>
          <span className="h-px flex-1 bg-gradient-to-r from-transparent via-white/15 to-white/5" aria-hidden="true" />
          <svg width="16" height="14" viewBox="0 0 18 16" className="shrink-0" aria-hidden="true">
            <path d="M9 1 L17 15 H1 Z" fill="none" stroke="#E60000" strokeWidth="1.4" />
          </svg>
          <span className="font-body text-[11px] font-medium uppercase tracking-[0.45em] text-ash">
            Also In Development
          </span>
          <svg width="16" height="14" viewBox="0 0 18 16" className="shrink-0" aria-hidden="true">
            <path d="M9 1 L17 15 H1 Z" fill="none" stroke="#E60000" strokeWidth="1.4" />
          </svg>
          <span className="h-px flex-1 bg-gradient-to-l from-transparent via-white/15 to-white/5" aria-hidden="true" />
        </FadeIn>

        <FadeIn className="mt-16" delay={0.1}>
          <article
            data-testid="neck-creeper-card"
            className="group relative grid grid-cols-1 overflow-hidden border border-white/10 bg-carbon transition-[border-color] duration-500 hover:border-ember/60 lg:grid-cols-12"
          >
            <div className="relative overflow-hidden lg:col-span-7">
              <img
                data-testid="neck-creeper-image"
                src="/neck-creeper.jpg"
                alt="Neck Creeper by Proxima Centauri Tools — work under, less pain, more jobs. Patent pending, coming soon"
                className="h-auto w-full object-cover transition-transform duration-700 group-hover:scale-[1.03] lg:h-full"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-coal/60 via-transparent to-transparent lg:bg-gradient-to-r lg:from-transparent lg:to-carbon/70" />
            </div>
            <div className="relative flex flex-col justify-center p-8 md:p-14 lg:col-span-5">
              <span className="absolute right-0 top-0 h-8 w-8 border-r-2 border-t-2 border-ember/50" aria-hidden="true" />
              <div className="mb-6 flex items-center gap-3">
                <span className="inline-block h-2 w-2 animate-pulse bg-ember red-glow-box" />
                <span className="font-body text-[11px] font-medium uppercase tracking-[0.35em] text-ember">
                  04 — Next Up
                </span>
              </div>
              <h3 data-testid="neck-creeper-heading" className="font-display text-[clamp(3rem,6vw,6rem)] font-semibold uppercase leading-[0.9] text-white">
                Neck <span className="metal-sheen">Creeper</span>
              </h3>
              <p className="mt-3 font-body text-xs font-medium uppercase tracking-[0.4em] text-steel">
                Work Under. Less Pain. More Jobs.
              </p>
              <p className="mt-6 max-w-md font-body text-base font-light leading-relaxed text-ash">
                Another product from Proxima Centauri Tools. Adjustable neck support and
                back rest on a low-profile frame — full reveal coming.
              </p>
              <div className="mt-9 flex flex-wrap items-center gap-4">
                <span
                  data-testid="neck-creeper-patent-badge"
                  className="border border-ember/60 px-5 py-2.5 font-display text-base font-semibold uppercase tracking-[0.2em] text-ember"
                >
                  Patent Pending
                </span>
                <span
                  data-testid="neck-creeper-coming-badge"
                  className="bg-ember px-5 py-2.5 font-display text-base font-semibold uppercase tracking-[0.2em] text-white red-glow-box"
                >
                  Coming Soon
                </span>
              </div>
              <Link
                data-testid="neck-creeper-explore-link"
                to="/neck-creeper"
                className="group/link mt-8 inline-flex w-fit items-center gap-2 border border-steel/30 px-7 py-3 font-display text-lg font-semibold uppercase tracking-[0.15em] text-steel transition-[border-color,color,background-color] duration-300 hover:border-ember hover:bg-ember/10 hover:text-white"
              >
                Explore Neck Creeper <ArrowUpRight size={18} className="transition-transform duration-300 group-hover/link:translate-x-1 group-hover/link:-translate-y-1" />
              </Link>
            </div>
          </article>
        </FadeIn>

        <FadeIn className="mt-24 flex items-center gap-6" delay={0.05}>
          <span className="h-px flex-1 bg-gradient-to-r from-transparent via-white/15 to-white/5" aria-hidden="true" />
          <svg width="16" height="14" viewBox="0 0 18 16" className="shrink-0" aria-hidden="true">
            <path d="M9 1 L17 15 H1 Z" fill="none" stroke="#E60000" strokeWidth="1.4" />
          </svg>
          <span className="font-body text-[11px] font-medium uppercase tracking-[0.45em] text-ash">
            Also In Development
          </span>
          <svg width="16" height="14" viewBox="0 0 18 16" className="shrink-0" aria-hidden="true">
            <path d="M9 1 L17 15 H1 Z" fill="none" stroke="#E60000" strokeWidth="1.4" />
          </svg>
          <span className="h-px flex-1 bg-gradient-to-l from-transparent via-white/15 to-white/5" aria-hidden="true" />
        </FadeIn>

        <FadeIn className="mt-16" delay={0.1}>
          <article
            data-testid="hornet-card"
            className="group relative grid grid-cols-1 overflow-hidden border border-white/10 bg-carbon transition-[border-color] duration-500 hover:border-ember/60 lg:grid-cols-12"
          >
            <div className="relative overflow-hidden lg:order-2 lg:col-span-7">
              <img
                data-testid="hornet-image"
                src="/hornet.jpg"
                alt="Hornet by Proxima Centauri Tools — the electrician's reamer and driver, patent pending, coming soon"
                className="h-auto w-full object-cover transition-transform duration-700 group-hover:scale-[1.03] lg:h-full"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-coal/60 via-transparent to-transparent lg:bg-gradient-to-l lg:from-transparent lg:to-carbon/70" />
            </div>
            <div className="relative flex flex-col justify-center p-8 md:p-14 lg:order-1 lg:col-span-5">
              <span className="absolute left-0 top-0 h-8 w-8 border-l-2 border-t-2 border-ember/50" aria-hidden="true" />
              <div className="mb-6 flex items-center gap-3">
                <span className="inline-block h-2 w-2 animate-pulse bg-ember red-glow-box" />
                <span className="font-body text-[11px] font-medium uppercase tracking-[0.35em] text-ember">
                  05 — In The Lab
                </span>
              </div>
              <h3 data-testid="hornet-heading" className="font-display text-[clamp(3rem,6vw,6rem)] font-semibold uppercase leading-[0.9] text-white">
                Hor<span className="metal-sheen">net</span>
              </h3>
              <p className="mt-3 font-body text-xs font-medium uppercase tracking-[0.4em] text-steel">
                Electrician's Tool — In Development
              </p>
              <p className="mt-6 max-w-md font-body text-base font-light leading-relaxed text-ash">
                The electrician's reamer and driver from Proxima Centauri Tools. One tool,
                two jobs — full reveal coming.
              </p>
              <div className="mt-9 flex flex-wrap items-center gap-4">
                <span
                  data-testid="hornet-patent-badge"
                  className="border border-ember/60 px-5 py-2.5 font-display text-base font-semibold uppercase tracking-[0.2em] text-ember"
                >
                  Patent Pending
                </span>
                <span
                  data-testid="hornet-coming-badge"
                  className="bg-ember px-5 py-2.5 font-display text-base font-semibold uppercase tracking-[0.2em] text-white red-glow-box"
                >
                  Coming Soon
                </span>
              </div>
              <Link
                data-testid="hornet-explore-link"
                to="/hornet"
                className="group/link mt-8 inline-flex w-fit items-center gap-2 border border-steel/30 px-7 py-3 font-display text-lg font-semibold uppercase tracking-[0.15em] text-steel transition-[border-color,color,background-color] duration-300 hover:border-ember hover:bg-ember/10 hover:text-white"
              >
                Explore Hornet <ArrowUpRight size={18} className="transition-transform duration-300 group-hover/link:translate-x-1 group-hover/link:-translate-y-1" />
              </Link>
            </div>
          </article>
        </FadeIn>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-12">
          <FadeIn className="lg:col-span-4 lg:col-start-9" delay={0.15}>
            <article
              data-testid="built-different-card"
              className="flex h-full min-h-[272px] flex-col justify-between border border-white/10 bg-carbon p-8 transition-[border-color] duration-500 hover:border-ember/50 md:p-10"
            >
              <TriangleAlert size={30} className="text-steel" strokeWidth={1.5} />
              <div>
                <h3 className="font-display text-4xl font-semibold uppercase leading-none text-white">Built Different</h3>
                <p className="mt-4 font-body text-sm font-light leading-relaxed text-ash">
                  No focus-group gadgets. No copycat designs. Tools that earn their
                  place in the box — engineered from experience, not trends.
                </p>
              </div>
            </article>
          </FadeIn>
        </div>
      </div>
    </section>
  );
};

export default ToolsSection;
